import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import Skeleton from '@mui/material/Skeleton';
import { CY_SPOTLIGHT_HEADER } from '../../../../lib/constants/cypress';
import { useViewContext, ViewContextType } from '../../../../lib/context/ViewContext';
import { ContentBlock } from '../../../../lib/types/aemDataModels';
import { WithValidated } from '../../../../lib/types/helperTypes';
import { arrayToClasses, classes } from '../../../../lib/utils/cssUtils';
import { replaceObjectPlaceholders } from '../../../../lib/utils/placeholders/replacePlaceholder';
import { MappedAEMProperties } from '../../../../types';
import PureImage, { PureImageProps } from '../../../common/Image/PureImage';
import PureRichText from '../../../common/PureRichText/PureRichText';
import styles from './SpotlightHeader.module.scss';
import { SCALE } from '../../../common/Image/ImageScale';
import { Asset } from '../../../common/model';
import Button from '../../../common/Button/Button';
import { LearningImage, LearningImageProps } from '../../../common/Image/LearningImage';
import { Label } from '../../../common/Label/Label';
import { SPOTLIGHT_HEADER_LABELS } from '../../../../lib/constants/labelKeys';
import { BackArrow } from '../../../../lib/icons/BackArrow';
import PureHeading from '../../../common/Heading/PureHeading';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import { spotlightHeaderIds } from '../../../../lib/tests/testIds';
import ErrorInfo from '../../base/ErrorInfo/ErrorInfo';
import PureLink, { PureLinkVariant } from '../../../common/Link/PureLink';
import { combineFunctions } from '../../../../lib/utils/functional';
import { Api } from '../../../../lib/types/apiSpec';
import { PageType } from '../../../../lib/hooks/usePageDetails';

type WidthVariant = 'narrow' | 'wide' | 'ultrawide';
type HeightVariant = 'regular' | 'short' | 'ultrashort';
type SecondaryVariant = `secondary-${'boostedorange' | 'primarynavy'}`;
export type CombinedVariants =
  | 'deep-blue'
  | 'boosted-blue'
  | 'dark-grey'
  | 'wrap'
  | 'divider-hidden'
  | WidthVariant
  | HeightVariant
  | SecondaryVariant;

const DEFAULT_WIDTH: WidthVariant = 'narrow';
const DEFAULT_HEIGHT: HeightVariant = 'short';
const DEFAULT_SECONDARY: SecondaryVariant = 'secondary-boostedorange';

export type AemSpotlightBannerProps = {
  variants: CombinedVariants[];
  contentBlock: ContentBlock | null;
  showBackButton: boolean;
  hideImage?: boolean;
} & MappedAEMProperties;

export type SpotlightBannerProps = WithValidated<AemSpotlightBannerProps, 'contentBlock'>;

enum ImgType {
  Litmos = 'litmos',
  Dam = 'dam',
}

type AssetPropsReturnType =
  | ({ imgType: ImgType.Litmos } & LearningImageProps)
  | ({ imgType: ImgType.Dam } & PureImageProps)
  | null;

const isDamImage = (
  imgType: ImgType | undefined,
  imgProps: Omit<AssetPropsReturnType, 'imgType'>,
): imgProps is PureImageProps => imgType === ImgType.Dam;
const isLitmosImage = (
  imgType: ImgType | undefined,
  imgProps: Omit<AssetPropsReturnType, 'imgType'>,
): imgProps is LearningImageProps => imgType === ImgType.Litmos;

const getAssetProps = (
  page: ViewContextType['page'],
  asset: Asset | undefined,
  widthVariant: WidthVariant,
  heightVariant: HeightVariant,
  showHiddenText?: boolean,
): AssetPropsReturnType => {
  const baseProps = {
    imageClassName: classes(styles.image, styles[widthVariant], styles[heightVariant]),
    className: classes(styles.imageContainer, { [styles.showHidden]: showHiddenText }),
    scale: SCALE.BANNER,
  };

  if (page.document) {
    return {
      ...baseProps,
      imgType: ImgType.Litmos,
      name: page.document.name,
      type: Api.LearningType.Document,
      imageURL: page.document.thumbnail,
    };
  }

  const { trainingProps, type } = (() => {
    if (page.type === PageType.LearningPathDetails && page.learningPath) {
      return { trainingProps: page.learningPath, type: Api.LearningType.LearningPath };
    }

    if (page.type === PageType.CourseDetails && page.course) {
      return { trainingProps: page.course, type: Api.LearningType.Course };
    }

    return { trainingProps: undefined, type: undefined };
  })();

  if (trainingProps) {
    return {
      ...baseProps,
      imgType: ImgType.Litmos,
      name: trainingProps.name ?? '',
      id: trainingProps.originalId,
      type,
      imageURL: trainingProps.imageURL,
    };
  }

  if (asset) {
    return {
      ...baseProps,
      imgType: ImgType.Dam,
      src: asset.path,
      height: asset.height,
      width: asset.width,
    };
  }

  return null;
};

const isWidth = (variant: any): variant is WidthVariant => {
  const widthVariants: WidthVariant[] = ['narrow', 'wide', 'ultrawide'];

  return widthVariants.includes(variant);
};

const isHeight = (variant: any): variant is HeightVariant => {
  const widthVariants: HeightVariant[] = ['regular', 'short', 'ultrashort'];

  return widthVariants.includes(variant);
};

const isSecondaryColor = (variant: any): variant is SecondaryVariant => {
  const secondaryVariants: SecondaryVariant[] = ['secondary-boostedorange', 'secondary-primarynavy'];

  return secondaryVariants.includes(variant);
};

const SpotlightBanner = ({ variants, contentBlock, showBackButton, isInEditor, hideImage }: SpotlightBannerProps) => {
  const { heading, content, asset, link, linkLabel } = contentBlock;
  const { app, page, isLoading } = useViewContext();

  const replacedContent = replaceObjectPlaceholders({ ...app, ...page }, content);
  const replacedHeading = replaceObjectPlaceholders({ ...app, ...page }, heading);

  const secondaryVariant: SecondaryVariant = variants.find(isSecondaryColor) ?? DEFAULT_SECONDARY;
  const widthVariant = variants.find(isWidth) ?? DEFAULT_WIDTH;
  const heightVariant = variants.find(isHeight) ?? DEFAULT_HEIGHT;
  const variantsWithDefaults = [
    ...variants.filter(variant => !combineFunctions([isWidth, isHeight, isSecondaryColor])(variant).find(Boolean)),
    secondaryVariant,
    widthVariant,
    heightVariant,
  ];

  const { imgType, ...imageSrc } = getAssetProps(page, asset, widthVariant, heightVariant) ?? {};
  const history = useHistory();

  const placeholderReg = /{{.+}}/;
  const headingContainsPlaceholder = placeholderReg.test(heading || '');
  const headingIsResolved = !placeholderReg.test(replacedHeading || '');
  const contentContainsPlaceholder = placeholderReg.test(content || '');
  const contentIsResolved = !placeholderReg.test(replacedContent || '');

  const headingPart = useMemo(() => {
    if (heading) {
      if (!headingContainsPlaceholder) {
        return replacedHeading;
      }

      if (isLoading) {
        return <Skeleton className={styles.skeletonHeading} />;
      }

      if (page.error) {
        return <ErrorInfo error={page.error} internal className={styles.errorContainer} />;
      }

      if (!isInEditor && !headingIsResolved) {
        return null;
      }

      return replacedHeading;
    }

    return null;
  }, [heading, replacedHeading, headingContainsPlaceholder, isLoading, headingIsResolved, isInEditor]);

  const contentPart = useMemo(() => {
    if (content) {
      const contentNode = (
        <>
          <PureRichText
            data-testid={spotlightHeaderIds.content}
            richtext={replacedContent || ''}
            richtextClass={styles.text}
            cypressDataAttr={CY_SPOTLIGHT_HEADER.RICHTEXT}
          />
          {!!link && (
            <PureLink href={link} variant={PureLinkVariant.Link} className={styles.link}>
              {linkLabel}
            </PureLink>
          )}
        </>
      );

      if (!contentContainsPlaceholder) {
        return contentNode;
      }
      if (isLoading) {
        return (
          <div className={styles.skeletonContent}>
            <Skeleton className={styles.skeletonCompleted} data-testid={spotlightHeaderIds.contentSkeleton} />
            <Skeleton className={styles.skeletonUpdated} />
          </div>
        );
      }

      if (page.error) {
        return <ErrorInfo error={page.error} internal className={styles.errorContainer} />;
      }

      if (!isInEditor && !contentIsResolved) {
        return null;
      }

      return contentNode;
    }

    return null;
  }, [content, replacedContent, contentContainsPlaceholder, isLoading, contentIsResolved, isInEditor]);

  return (
    <section className={classes(styles.section, arrayToClasses(variantsWithDefaults, styles))}>
      <div className={styles.container}>
        <div
          className={classes(styles.headingContainer, styles[widthVariant], {
            [styles.withBackButton]: showBackButton,
          })}
        >
          {showBackButton && (
            <Button
              onClick={() => history.go(-1)}
              startIcon={<BackArrow />}
              variant="text"
              className={styles.button}
              classes={{ startIcon: styles.buttonIcon }}
            >
              <Label name={SPOTLIGHT_HEADER_LABELS.BACK_BUTTON} />
            </Button>
          )}
          {!!heading && (
            <PureHeading
              level={HeadingLevel.h1}
              className={classes(styles.heading, { [styles.wrap]: variants.includes('wrap') })}
              title={replacedHeading}
            >
              {headingPart}
            </PureHeading>
          )}
          {!!headingPart && !!contentPart && !variants.includes('divider-hidden') && (
            <div data-testid={spotlightHeaderIds.divider} className={styles.divider} />
          )}
          {contentPart}
        </div>
        {!hideImage &&
          (isLoading ? (
            <div className={styles.imageContainer}>
              <Skeleton
                variant="rectangular"
                className={classes(styles.image, styles[widthVariant], styles[heightVariant], styles.skeletonImage)}
              />
            </div>
          ) : (
            <>
              {isDamImage(imgType, imageSrc) && <PureImage {...imageSrc} />}
              {isLitmosImage(imgType, imageSrc) && <LearningImage {...imageSrc} />}
            </>
          ))}
      </div>
    </section>
  );
};

export default SpotlightBanner;
